import {Injectable} from '@angular/core';
import {Auth0Client, createAuth0Client, GetTokenSilentlyVerboseResponse} from '@auth0/auth0-spa-js';
import {BehaviorSubject, combineLatest, EMPTY, from, fromEvent, Observable, of, throwError} from 'rxjs';
import {catchError, concatMap, finalize, map, shareReplay, take, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {fromFetch} from 'rxjs/fetch';
import {HttpService} from './http.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private router: Router,
    private httpService: HttpService
  ) {
    this.bindCrossTabSync();

    const params = window.location.search;
    const isAuthCallback = this.isAuthCallbackUrl();
    const isPostLogout = params.includes('postLogout=true');
    const isReauth = params.includes('reauth=1');

    if (isAuthCallback) {
      this.handleAuthCallback();
      return;
    }

    if (isPostLogout) {
      this.clearLocalSession(false);
      this.redirectInProgress = false;

      this.login('/');

      return;
    }

    if (isReauth) {
      this.clearLocalSession(false);

      const target = new URLSearchParams(window.location.search).get('target') || '/';

      // setTimeout(() => {
      this.redirectInProgress = false;
      this.login(target);
      // }, 0);

      return;
    }

    this.localAuthSetup();
  }
  private static readonly ACCESS_TOKEN_KEY = 'auth.access_token';
  private static readonly ACCESS_TOKEN_EXPIRES_AT_KEY = 'auth.access_token_expires_at';
  private static readonly LOGOUT_EVENT_KEY = 'auth.logout_event';
  private static readonly REAUTH_GUARD_KEY = 'auth.reauth_guard';
  private static readonly REAUTH_GUARD_WINDOW_MS = 30_000;
  private static readonly REAUTH_GUARD_LIMIT = 2;
  private static readonly REAUTH_ERRORS = ['login_required', 'missing_refresh_token', 'invalid_grant'];
  private static readonly CONSENT_ERRORS = ['consent_required', 'interaction_required'];
  auth0Client$ = (from(
    createAuth0Client({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri: `${window.location.origin}`,
        audience: environment.auth0Audience
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true
    })
  ) as Observable<Auth0Client>).pipe(
    shareReplay(1),
    catchError(err => throwError(() => err))
  );
  loggedIn: boolean = null;
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap(res => (this.loggedIn = res)),
    shareReplay(1)
  );
  private readonly renewSkewMs = 60 * 1000;
  private readonly defaultAccessTokenLifetimeSec = 1800;
  private redirectInProgress = false;
  private refreshTokenRequest$: Observable<string> | null = null;
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();

  private accessTokenSubject$ = new BehaviorSubject<string | null>(this.readStoredAccessToken());
  accessToken$ = this.accessTokenSubject$.asObservable();
  private handleRedirectCallback$(callbackUrl: string) {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback(callbackUrl)))
    );
  }

  getUser$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap(user => this.userProfileSubject$.next(user))
    );
  }

  getTokenSilently$(): Observable<string> {
    return this.ensureValidAccessToken$();
  }

  ensureValidAccessToken$(): Observable<string> {
    if (this.isAuthTransitionInProgress()) {
      console.warn('[AuthService] ensureValidAccessToken$: auth transition in progress, skip token request');
      return EMPTY;
    }

    const token = this.readStoredAccessToken();
    const expiresAt = this.readStoredAccessTokenExpiresAt();
    const now = Date.now();

    if (token && expiresAt && now < (expiresAt - this.renewSkewMs)) {
      this.accessTokenSubject$.next(token);
      return of(token);
    }

    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    this.refreshTokenRequest$ = this.renewAccessToken$().pipe(
      shareReplay(1),
      finalize(() => {
        this.refreshTokenRequest$ = null;
      })
    );

    return this.refreshTokenRequest$;
  }

  login(redirectPath: string = '/') {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;

    this.auth0Client$.pipe(take(1)).subscribe((client: Auth0Client) => {
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}`,
          audience: environment.auth0Audience
        },
        appState: {target: redirectPath}
      });
    });
  }

  logout() {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;
    this.clearLocalSession(true);

    this.auth0Client$.pipe(take(1)).subscribe((client: Auth0Client) => {
      client.logout({
        logoutParams: {
          returnTo: `${window.location.origin}?postLogout=true`
        }
      });
    });
  }

  forceReauth(targetPath: string = '/') {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;
    this.clearLocalSession(true);

    this.auth0Client$.pipe(take(1)).subscribe((client: Auth0Client) => {
      client.logout({
        logoutParams: {
          returnTo: `${window.location.origin}?reauth=1&target=${encodeURIComponent(targetPath)}`
        }
      });
    });
  }

  private triggerConsentRedirect(targetPath: string = '/') {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;

    this.auth0Client$.pipe(take(1)).subscribe((client: Auth0Client) => {
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}`,
          audience: environment.auth0Audience,
          prompt: 'consent'
        },
        appState: {target: targetPath}
      });
    });
  }

  private recordReauthAttempt(code: string): boolean {
    try {
      const raw = sessionStorage.getItem(AuthService.REAUTH_GUARD_KEY);
      const now = Date.now();

      let entry: { code: string; at: number; count: number };

      if (raw) {
        const parsed = JSON.parse(raw) as { code: string; at: number; count: number };
        const withinWindow = now - parsed.at < AuthService.REAUTH_GUARD_WINDOW_MS;

        if (parsed.code === code && withinWindow) {
          entry = {code, at: parsed.at, count: parsed.count + 1};
        } else {
          entry = {code, at: now, count: 1};
        }
      } else {
        entry = {code, at: now, count: 1};
      }

      sessionStorage.setItem(AuthService.REAUTH_GUARD_KEY, JSON.stringify(entry));
      return entry.count > AuthService.REAUTH_GUARD_LIMIT;
    } catch (e) {
      console.warn('[AuthService] recordReauthAttempt: sessionStorage unavailable, skipping guard', e);
      return false;
    }
  }

  private clearReauthGuard(): void {
    try {
      sessionStorage.removeItem(AuthService.REAUTH_GUARD_KEY);
    } catch (e) {
      console.warn('[AuthService] clearReauthGuard: sessionStorage unavailable', e);
    }
  }

  private renewAccessToken$(): Observable<string> {
    return this.auth0Client$.pipe(
      take(1),
      concatMap((client: Auth0Client) =>
        from(
          client.getTokenSilently({
            detailedResponse: true,
            authorizationParams: {
              audience: environment.auth0Audience
            }
          })
        ).pipe(
          map((res: string | GetTokenSilentlyVerboseResponse) => {
            let token: string;
            let expiresInSec: number;

            if (typeof res === 'string') {
              token = res;
              expiresInSec = this.defaultAccessTokenLifetimeSec;

              console.warn(
                '[AuthService] renewAccessToken$: string response received, using fallback lifetime sec =',
                expiresInSec
              );
            } else {
              token = res.access_token;
              expiresInSec =
                typeof res.expires_in === 'number'
                  ? res.expires_in
                  : this.defaultAccessTokenLifetimeSec;
            }

            const now = Date.now();
            const expiresAt = now + expiresInSec * 1000;

            this.storeAccessToken(token, expiresAt);
            return token;
          }),
          catchError(err => {
            console.error(
              '[AuthService] renewAccessToken$ error:',
              err?.error,
              '| status:',
              err?.status,
              '| message:',
              err?.message,
              '| full error:',
              err
            );

            const code: string | undefined = err?.error;
            const messageHasMissingRefresh =
              typeof err?.message === 'string' && err.message.includes('missing_refresh_token');

            const isReauthError = AuthService.REAUTH_ERRORS.includes(code) || messageHasMissingRefresh;
            const isConsentError = AuthService.CONSENT_ERRORS.includes(code);

            if (!isReauthError && !isConsentError) {
              return throwError(() => err);
            }

            if (this.isLogoutFlow()) {
              console.warn(
                '[AuthService] renewAccessToken$:',
                code,
                'during logout/reauth flow, ignoring'
              );
              return EMPTY;
            }

            const guardCode = code || (messageHasMissingRefresh ? 'missing_refresh_token' : 'unknown');
            if (this.recordReauthAttempt(guardCode)) {
              console.error(
                '[AuthService] reauth loop suspected, aborting redirect for code =',
                guardCode
              );
              return throwError(() => err);
            }

            if (isConsentError) {
              this.triggerConsentRedirect(window.location.pathname);
            } else {
              this.forceReauth(window.location.pathname);
            }

            return EMPTY;
          })
        )
      )
    );
  }

  private getUserProfile(accessToken: string): Observable<any> {
    return fromFetch('https://dev-ut9hcoi3.auth0.com/userinfo', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).pipe(
      concatMap(response => {
        if (response.ok) {
          return response.json();
        }
        return of({error: true, message: `Error ${response.status}`});
      }),
      catchError(err => {
        console.error(err);
        return of({error: true, message: err.message});
      })
    );
  }

  private localAuthSetup() {
    const checkAuth$ = this.isAuthenticated$.pipe(
      take(1),
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          return this.getUser$();
        }
        return of(loggedIn);
      })
    );

    checkAuth$.subscribe();
  }

  private handleAuthCallback() {
    if (!this.isAuthCallbackUrl()) {
      return;
    }

    const callbackUrl = window.location.href;
    let targetRoute = '/';

    const authComplete$ = this.handleRedirectCallback$(callbackUrl).pipe(
      tap(cbRes => {
        targetRoute = cbRes?.appState?.target || '/';
        this.clearReauthGuard();
      }),
      concatMap(() => combineLatest([this.getUser$(), this.isAuthenticated$.pipe(take(1))])),
      concatMap(([user, loggedIn]) => {
        if (!loggedIn) {
          return of([user, loggedIn]);
        }

        return this.renewAccessToken$().pipe(
          map(token => [user, loggedIn, token] as any)
        );
      })
    );

    authComplete$.subscribe({
      next: () => {
        this.saveRoles();

        const cleanUrl = window.location.origin + '/#' + (targetRoute || '/');
        window.history.replaceState({}, document.title, cleanUrl);

        if (targetRoute && targetRoute !== window.location.pathname) {
          this.router.navigateByUrl(targetRoute);
        }
      },
      error: err => {
        console.error('[AuthService] handleAuthCallback error:', err);
        this.clearLocalSession(false);
      }
    });
  }

  private saveRoles() {
    this.getUser$().pipe(take(1)).subscribe(user => {
      if (!user?.sub) {
        console.warn('[AuthService] saveRoles: no user sub, skipping');
        return;
      }

      this.httpService.getUserRoleListByAuth0Id(user.sub).subscribe(
        roles => {
          if (roles && roles.length > 0) {
            localStorage.setItem('userRoles', roles.join(','));

            this.httpService.getCustomerByAuth0Id(user.sub).subscribe(
              customer => {
                if (customer) {
                  localStorage.setItem('currentCustomer', JSON.stringify(customer));
                  window.location.reload();
                }
              },
              error => {
                console.error('getCustomerByAuth0Id error = ' + error);
                this.logout();
              }
            );
          } else {
            localStorage.setItem('userRoles', '[]');
          }
        },
        error => {
          console.error('getUserRoleListByAuth0Id error = ' + error);
          this.logout();
        }
      );
    });
  }

  private storeAccessToken(token: string, expiresAt: number): void {
    localStorage.setItem(AuthService.ACCESS_TOKEN_KEY, token);
    localStorage.setItem(AuthService.ACCESS_TOKEN_EXPIRES_AT_KEY, String(expiresAt));
    this.accessTokenSubject$.next(token);
  }

  private readStoredAccessToken(): string | null {
    return localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
  }

  private readStoredAccessTokenExpiresAt(): number | null {
    const value = localStorage.getItem(AuthService.ACCESS_TOKEN_EXPIRES_AT_KEY);
    return value ? Number(value) : null;
  }

  private clearLocalSession(broadcast: boolean): void {
    localStorage.removeItem(AuthService.ACCESS_TOKEN_KEY);
    localStorage.removeItem(AuthService.ACCESS_TOKEN_EXPIRES_AT_KEY);
    localStorage.removeItem('userRoles');
    localStorage.removeItem('currentCustomer');

    this.accessTokenSubject$.next(null);

    if (broadcast) {
      localStorage.setItem(AuthService.LOGOUT_EVENT_KEY, JSON.stringify({at: Date.now()}));
    }
  }

  private bindCrossTabSync(): void {
    fromEvent<StorageEvent>(window, 'storage').subscribe(event => {
      if (event.key === AuthService.LOGOUT_EVENT_KEY && event.newValue) {
        this.clearLocalSession(false);

        if (!this.redirectInProgress) {
          this.redirectInProgress = true;
          window.location.assign(`${window.location.origin}?postLogout=true`);
        }
      }

      if (event.key === AuthService.ACCESS_TOKEN_KEY && event.newValue) {
        this.accessTokenSubject$.next(event.newValue);
      }

      if (event.key === AuthService.ACCESS_TOKEN_KEY && !event.newValue) {
        this.accessTokenSubject$.next(null);
      }
    });
  }

  private isAuthCallbackUrl(): boolean {
    const params = window.location.search;
    return params.includes('code=') && params.includes('state=');
  }

  private isLogoutFlow(): boolean {
    const params = window.location.search;
    return params.includes('postLogout=true') || params.includes('reauth=1');
  }

  private isAuthTransitionInProgress(): boolean {
    return this.isAuthCallbackUrl() || this.isLogoutFlow();
  }
}
